// src/components/VideoModal.tsx
//
// Self-contained, reusable video player modal with an optional tab switcher
// for multiple videos. Used by the landing page ("Watch Video" button).
//
// Playback is now delegated to YouTube via an embedded <iframe>. This removes
// the old large-local-file stalling problem entirely (YouTube streams
// adaptively) and costs zero hosting bandwidth.
//
//   • Responsive 16:9 wrapper — the iframe scales with the modal width.
//   • allowFullScreen          — the viewer can go fullscreen.
//   • key={current.src}        — the <iframe> only remounts when the SELECTED
//                                video changes, so re-renders of the parent
//                                page do not reset playback.
//
// `src` accepts any common YouTube form (watch?v=, youtu.be/, /embed/, or a
// bare 11-char ID); youTubeId() normalizes it to an embed URL.

import React, { useState } from "react";
import { C, FONT } from "../theme";

export interface VideoItem {
  /** YouTube reference: watch URL, youtu.be URL, /embed/ URL, or a bare ID. */
  src: string;
  /** Tab label — only shown when there is more than one video. */
  label: string;
}

interface Props {
  title: string;
  videos: VideoItem[];
  onClose: () => void;
  /** Retained for backward compatibility with existing callers; unused with
   *  YouTube embeds (an iframe has no reliable load-error event). */
  unavailableLabel?: string;
}

/** Extracts the YouTube video ID from common URL forms (or a bare ID). */
function youTubeId(src: string): string {
  const s = (src || "").trim();
  const short = s.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (short) return short[1];
  const watch = s.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  if (watch) return watch[1];
  const embed = s.match(/\/embed\/([A-Za-z0-9_-]{6,})/);
  if (embed) return embed[1];
  return s; // already a bare ID
}

/** Builds the privacy-friendly embed URL for a given YouTube reference. */
function embedUrl(src: string): string {
  return `https://www.youtube-nocookie.com/embed/${youTubeId(src)}?rel=0&modestbranding=1`;
}

export default function VideoModal({ title, videos, onClose }: Props) {
  const [active, setActive] = useState(0);
  const current = videos[active] ?? videos[0];

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {videos.length > 1 && (
          <div style={s.tabs}>
            {videos.map((v, i) => (
              <button
                key={v.src}
                style={{ ...s.tab, ...(i === active ? s.tabActive : {}) }}
                onClick={() => setActive(i)}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        {/* Responsive 16:9 wrapper */}
        <div style={s.videoWrap}>
          <iframe
            key={current.src}
            src={embedUrl(current.src)}
            title={current.label || title}
            style={s.iframe}
            frameBorder={0}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: 20,
  },
  modal: {
    backgroundColor: "#000",
    borderRadius: 14,
    overflow: "hidden",
    width: "100%",
    maxWidth: 900,
    boxShadow: "0 40px 100px rgba(0,0,0,0.8)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 18px",
    backgroundColor: "#0d0d0d",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  title: {
    color: "rgba(255,255,255,0.85)",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.5px",
    fontFamily: FONT.body,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#aaa",
    fontSize: 18,
    cursor: "pointer",
    lineHeight: 1,
    padding: 4,
  },
  tabs: {
    display: "flex",
    gap: 8,
    padding: "10px 18px",
    backgroundColor: "#0d0d0d",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    flexWrap: "wrap",
  },
  tab: {
    backgroundColor: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.7)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
  },
  tabActive: {
    backgroundColor: C.red,
    color: "#fff",
    borderColor: C.red,
  },
  // Responsive 16:9 container — the iframe is absolutely positioned to fill it.
  videoWrap: {
    position: "relative",
    width: "100%",
    paddingBottom: "56.25%", // 9 / 16
    height: 0,
    backgroundColor: "#000",
  },
  iframe: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: 0,
    display: "block",
  },
};
